// What a client sees of a BA profile, and the labels that must never be missed
// (BA P1.6a): whether a preview is the draft or the live version, and which
// facts the BA wrote vs which the company checked.
//
// Owner AC3: <ClientProfileView> renders ONLY a client-view response (P1.8's
// preview `view` / P1.3's catalogue projection — the same keys). It never takes
// employee data and never merges anything client-side. The reviewer's revision
// comparison is a different component (<RevisionView>), never labelled as what
// clients see.
import React from 'react';
import { useTranslation } from 'react-i18next';
import { BadgeCheck, Eye, PencilLine, UserRound } from 'lucide-react';
import { Badge } from '@/features/shared/components/ui/badge';
import type { BaClientView, BaContent, VerificationMark } from '@/services/baProfileService';
import { ContentField, PreviewSource, Verification, verificationOf } from '../portfolio';

/** The unmissable label on every preview. Only `published` may say clients see
 *  it; any other source — draft, a non-live revision, or anything unknown — is
 *  labelled as not seen by clients. */
export const PreviewLabel: React.FC<{ source: PreviewSource }> = ({ source }) => {
  const { t } = useTranslation('brandAmbassadors');
  const live = source === 'published';
  return (
    <div
      role="status"
      data-testid="preview-label"
      data-source={source}
      className={`flex items-center gap-2 rounded-md px-3 py-2 text-sm font-semibold ${
        live ? 'bg-success-soft text-success-strong' : 'bg-warning-soft text-warning-strong'}`}
    >
      {live ? <Eye className="w-4 h-4" /> : <PencilLine className="w-4 h-4" />}
      {live
        ? t('preview.published_label', { defaultValue: 'Published: what clients see now' })
        : source === 'draft'
          ? t('preview.draft_label', { defaultValue: 'Draft: clients don’t see this yet' })
          : t('preview.revision_label', { defaultValue: 'Not published: clients don’t see this version' })}
    </div>
  );
};

/** BA-provided vs company-verified. `self`: the BA's own editor ("Provided by you"). */
export const VerificationLabel: React.FC<{ value: Verification; company: string; self?: boolean }> = ({ value, company, self }) => {
  const { t } = useTranslation('brandAmbassadors');
  return value === 'fern_verified' ? (
    <Badge variant="success" data-testid="verification" data-value={value}>
      <BadgeCheck className="w-3 h-3 mr-1 inline" />
      {t('verification.verified', { defaultValue: '{{company}} verified', company })}
    </Badge>
  ) : (
    <Badge variant="neutral" data-testid="verification" data-value={value}>
      {self
        ? t('verification.provided_by_you', { defaultValue: 'Provided by you' })
        : t('verification.provided', { defaultValue: 'BA-provided' })}
    </Badge>
  );
};

const List: React.FC<{ items?: string[] | null }> = ({ items }) =>
  items && items.length ? (
    <div className="flex flex-wrap gap-1.5">{items.map((s) => <Badge key={s} variant="outline">{s}</Badge>)}</div>
  ) : <span className="text-sm text-muted-foreground">—</span>;

type Labeller = (f: ContentField, item?: number) => React.ReactNode;

/** The fields, in client order, each with whatever label the caller supplies. */
const Fields: React.FC<{ content: BaContent; label: Labeller; highlight?: ContentField[] }> = ({ content, label, highlight = [] }) => {
  const { t } = useTranslation('brandAmbassadors');
  const row = (f: ContentField, title: string, body: React.ReactNode) => (
    <section key={f} data-field={f} data-changed={highlight.includes(f) || undefined}
      className={`space-y-1 rounded-md p-2 ${highlight.includes(f) ? 'bg-info-soft' : ''}`}>
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
        {f !== 'experience' && label(f)}
      </div>
      {body}
    </section>
  );
  return (
    <>
      <div className="flex items-center gap-3">
        <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center" aria-hidden>
          <UserRound className="w-7 h-7 text-muted-foreground" />
        </div>
        <p className="text-lg font-semibold text-foreground">{content.display_name || '—'}</p>
      </div>
      {row('bio', t('fields.bio', { defaultValue: 'Introduction' }),
        <p className="text-sm text-foreground whitespace-pre-line">{content.bio || '—'}</p>)}
      {row('skills', t('fields.skills', { defaultValue: 'Skills' }), <List items={content.skills} />)}
      {row('languages', t('fields.languages', { defaultValue: 'Languages' }), <List items={content.languages} />)}
      {row('service_areas', t('fields.service_areas', { defaultValue: 'Service areas' }), <List items={content.service_areas} />)}
      {row('experience', t('fields.experience', { defaultValue: 'Experience' }),
        content.experience && content.experience.length ? (
          <ul className="space-y-1 text-sm text-foreground">
            {content.experience.map((e, i) => (
              <li key={i} data-item={i} className="flex items-start justify-between gap-2">
                <span>
                  <span className="font-medium">{e.title}</span>
                  {e.year ? <span className="text-muted-foreground"> · {e.year}</span> : null}
                  {e.detail ? <span className="block text-muted-foreground">{e.detail}</span> : null}
                </span>
                {label('experience', i)}
              </li>
            ))}
          </ul>
        ) : <span className="text-sm text-muted-foreground">—</span>)}
    </>
  );
};

/** What clients see: a client-view response, and nothing else (AC3). */
export const ClientProfileView: React.FC<{ view: BaClientView; source: PreviewSource; company: string }> = ({ view, source, company }) => (
  <div className="space-y-3" data-testid="client-view">
    <PreviewLabel source={source} />
    <Fields content={view.content || {}}
      label={(f, item) => <VerificationLabel value={verificationOf(f, view.verification, item)} company={company} />} />
  </div>
);

const when = (iso: string) => new Date(iso).toLocaleDateString();

/** Reviewer-only: a revision's content, changed fields highlighted, and each
 *  mark with WHO verified it and WHEN (P1.8). Never shown to clients or the BA. */
export const RevisionView: React.FC<{
  content: BaContent; marks?: VerificationMark[]; company: string; highlight?: ContentField[];
}> = ({ content, marks = [], company, highlight }) => {
  const { t } = useTranslation('brandAmbassadors');
  const markOf = (f: ContentField, item?: number) =>
    marks.find((m) => m.field === f && (f !== 'experience' || m.item === item));
  return (
    <div className="space-y-3" data-testid="revision-view">
      <Fields content={content} highlight={highlight} label={(f, item) => {
        const m = markOf(f, item);
        if (!m) return <VerificationLabel value="ba_provided" company={company} />;
        return (
          <span className="flex items-center gap-1">
            <VerificationLabel value="fern_verified" company={company} />
            <span className="text-xs text-muted-foreground" data-testid="verified-by">
              {t(m.carried ? 'review.verified_by_carried' : 'review.verified_by', {
                defaultValue: m.carried ? '{{who}}, {{date}} (carried)' : '{{who}}, {{date}}',
                who: m.verified_by_name || m.verified_by, date: when(m.verified_at),
              })}
            </span>
          </span>
        );
      }} />
    </div>
  );
};
